<div class="bk-page mr">
  <a class="mr__crumb" [routerLink]="['/organization/messages', uuid]">
    <span class="bk-sym" aria-hidden="true">arrow_back</span>
    Message
  </a>

  <!-- The send this report is about, and what can be done with its receipts -->
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <p class="bk-eyebrow">Delivery report</p>
      @if (messageState === "loading") {
        <div class="mr__title-skel" aria-hidden="true">
          <span class="bk-skel" style="width: min(420px, 80%); height: 32px"></span>
          <span class="bk-skel" style="width: min(300px, 60%); height: 14px"></span>
        </div>
      } @else {
        <h1 class="bk-page__title">{{ pageTitle }}</h1>
        @if (messageState === "error") {
          <p class="bk-page__sub">
            The subject and send details could not be loaded. Its delivery receipts are below.
          </p>
        } @else {
          <p class="mr__meta">
            @if (status) {
              <app-status [text]="status" [status]="status" />
            }
            @if (whenText) {
              <span [attr.title]="whenExact || null">{{ whenLabel }} {{ whenText }}</span>
            }
            @if (from) {
              <span>from {{ from }}</span>
            }
            @if (partsText) {
              <span>{{ partsText }} per recipient</span>
            }
          </p>
        }
      }
    </div>

    <div class="bk-page__actions mr__actions">
      <ng-container *hasPermission="{ category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'compose-sms' }">
        @if (failedCount > 0) {
          <button
            type="button"
            class="bk-btn bk-btn--ghost"
            [attr.aria-disabled]="resendBusy || null"
            [attr.aria-busy]="resendBusy || null"
            matTooltip="Opens a new message with the failed numbers filled in. Nothing is sent until you send it."
            (click)="resendToFailed()"
          >
            <span class="bk-sym" aria-hidden="true">replay</span>
            {{ resendBusy ? "Collecting numbers…" : "Resend to failed" }}
          </button>
        }
      </ng-container>
      <ng-container *hasPermission="{ category: 'REPORTS_AND_ANALYTICS', permission: 'export-reports-pdf-excel' }">
        <button
          type="button"
          class="bk-btn bk-btn--primary"
          [attr.aria-disabled]="exportBusy || null"
          [attr.aria-busy]="exportBusy || null"
          (click)="exportRows()"
        >
          <span class="bk-sym" aria-hidden="true">download</span>
          {{ exportBusy ? "Preparing…" : exportLabel }}
        </button>
      </ng-container>
    </div>
  </header>

  <!-- Export and resend progress -->
  <div class="mr__notes" aria-live="polite">
    @if (exportNote) {
      <p class="mr__note">
        <span class="bk-sym" aria-hidden="true">{{ exportBusy ? "hourglass_top" : "task_alt" }}</span>
        @if (exportBusy) {
          <!-- The row count changes every page: announce the start only, then the result. -->
          <span aria-hidden="true">{{ exportNote }}</span>
          <span class="bk-sr-only">Preparing the file…</span>
        } @else {
          {{ exportNote }}
        }
      </p>
    }
    @if (exportError) {
      <p class="mr__note is-error">
        <span class="bk-sym" aria-hidden="true">error</span>
        {{ exportError }}
      </p>
    }
    @if (resendError) {
      <p class="mr__note is-error">
        <span class="bk-sym" aria-hidden="true">error</span>
        {{ resendError }}
      </p>
    }
  </div>

  <!-- Delivery summary: tiles filter the list below -->
  <section class="bk-panel mr__summary" aria-labelledby="mr-delivery">
    <h2 class="bk-eyebrow" id="mr-delivery">Delivery</h2>
    <message-delivery-summary
      [summary]="summary"
      [state]="summaryState"
      [status]="status"
      [sentAt]="sentAt"
      [blacklisted]="blacklisted"
      mode="filter"
      [active]="filter"
      (pick)="pickTile($event)"
    />
  </section>

  <!-- Recipients -->
  <section class="mr__section" aria-labelledby="mr-recipients">
    <div class="mr__section-head">
      <h2 class="bk-section-title" id="mr-recipients">{{ listTitle }}</h2>
      @if (knownTotal !== null && !loading) {
        <span class="mr__count">{{ formatCount(knownTotal) }}</span>
      }
    </div>
    <p class="bk-sr-only" role="status">{{ loading ? "" : resultsAnnouncement }}</p>

    <div class="bk-toolbar mr__toolbar">
      <app-search-input label="Search recipients"
        class="mr__search"
        placeholder="Search by name or number"
        [debounce]="400"
        (valueChange)="updateSearchTerm($event)"
      />
      <app-status-filter
        label="Delivery"
        [options]="filterOptions"
        [selectedValue]="filter"
        (selectionChange)="updateFilter($event)"
      />
      <app-sort-dropdown
        [groups]="sortGroups"
        [selectedValue]="arrangementSort"
        (selectionChange)="updateSortArrangementCriteria($any($event))"
      />
    </div>

    <div class="bk-panel mr__panel">
      <app-message-reports-table
        [data]="messageReport"
        [loading]="loading"
        [query]="searchTerm"
        [filtered]="filter !== 'all'"
        [emptyMessage]="emptyMessage"
        [emptyHint]="emptyHint"
        [filteredHint]="loadError ? emptyHint : ''"
        (clear)="clearFilters()"
      />
    </div>

    @if (messageReport.length > 0 || page > 0) {
      <div class="mr__pagination">
        <app-pagination
          [currentPage]="page"
          [totalPages]="totalPages"
          [totalItems]="knownTotal ?? undefined"
          [pageSize]="pageSize"
          [loading]="loading"
          (changePage)="goToPage($event)"
          (onNext)="goToPage(page + 1)"
          (onPrev)="goToPage(page - 1)"
          (pageSizeChange)="onPageSizeChange($event)"
        />
      </div>
    }
  </section>

  <!-- Audience charts -->
  <section class="mr__section" aria-labelledby="mr-audience">
    <h2 class="bk-section-title" id="mr-audience">Who received it</h2>
    @if (loadingCharts) {
      <div class="mr__charts" aria-hidden="true">
        <span class="bk-skel mr__chart mr__chart--wide mr__chart-skel"></span>
        <span class="bk-skel mr__chart mr__chart-skel"></span>
        <span class="bk-skel mr__chart mr__chart-skel"></span>
      </div>
    } @else if (chartsError) {
      <p class="bk-panel mr__quiet">The charts could not be loaded. Refresh the page to try again.</p>
    } @else if (messageCharts) {
      <div class="mr__charts">
        <div class="mr__chart mr__chart--wide">
          <app-delivery-rate-by-recipients-graph
            [graphData]="messageCharts.deliveryRateByGroupReport || []"
          ></app-delivery-rate-by-recipients-graph>
        </div>
        @if (messageCharts.genderDistributionPieReport) {
          <div class="mr__chart">
            <app-gender-distribution-graph
              [graphData]="messageCharts.genderDistributionPieReport"
            ></app-gender-distribution-graph>
          </div>
        }
        @if (messageCharts.ageDistributionPieReport) {
          <div class="mr__chart">
            <app-age-group-distribution-graph
              [graphData]="messageCharts.ageDistributionPieReport"
            ></app-age-group-distribution-graph>
          </div>
        }
      </div>
    } @else {
      <p class="bk-panel mr__quiet">Charts appear here once delivery receipts come in.</p>
    }
  </section>
</div>

<!-- Resend: only asked when the numbers cannot travel in the link -->
<app-prompt-card
  title="Resend to failed numbers"
  buttonText="Open new message"
  tone="default"
  [openModal]="!!resendPrompt"
  (onClose)="closeResendPrompt()"
  (onButtonClick)="confirmResend()"
>
  <div class="mr__prompt">
    <p>
      {{ formatCount(resendCount) }} failed {{ resendCount === 1 ? "number goes" : "numbers go" }}
      into a new message with the subject filled in. Nothing is sent until you
      review it and send it.
    </p>
    @if (resendViaState) {
      <p>
        That is too many to carry in a link, so the numbers are handed to the new
        message directly. Reloading that page clears them: export the failed
        numbers first if you need a copy.
      </p>
    }
    @if (resendCapped) {
      <p>Only the first {{ formatCount(collectCap) }} failed rows are included.</p>
    }
    @if (resendBlacklisted) {
      <p>
        {{ formatCount(resendBlacklisted) }} blacklisted
        {{ resendBlacklisted === 1 ? "number is" : "numbers are" }} left out.
      </p>
    }
  </div>
</app-prompt-card>
